import { useState, useCallback } from 'react'
import type { AdminStats, Submission, PatientRecord } from '../lib/types'

interface UseAdminData {
  data: Submission[]
  generatedAt: string | null
  token: string
  isAuthed: boolean
  error: string
  loading: boolean
  setToken: (t: string) => void
  login: (token: string) => Promise<void>
  fetchPatient: (id: number) => Promise<PatientRecord>
}

export function useAdminData(): UseAdminData {
  const [data, setData]               = useState<Submission[]>([])
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)
  const [token, setToken]             = useState('')
  const [isAuthed, setIsAuthed]       = useState(false)
  const [error, setError]             = useState('')
  const [loading, setLoading]         = useState(false)

  const login = useCallback(async (tok: string) => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch('/api/admin/stats', {
        headers: { Authorization: `Bearer ${tok}` },
      })
      if (res.status === 401) {
        setError('Invalid token.')
        return
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json: AdminStats = await res.json()
      setData(json.submissions)
      setGeneratedAt(json.generatedAt)
      setToken(tok)
      setIsAuthed(true)
    } catch (e) {
      console.error(e)
      setError('Failed to load. Check console.')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPatient = useCallback(async (id: number): Promise<PatientRecord> => {
    const res = await fetch(`/api/admin/submissions/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
  }, [token])

  return { data, generatedAt, token, isAuthed, error, loading, setToken, login, fetchPatient }
}
